import { chunk, groupBy, times } from "@common/helpers/collections";
import {
    TASK_BULK_UPDATE_SYNC_THRESHOLD,
    type TaskBulkUpdateAction,
} from "@common/schemas/task.schema";
import type { Task } from "@common/types/task";
import {
    isTaskBulkUpdateApplied,
    type TaskBulkUpdateResponse,
} from "./taskUpdateCache";

export const MAX_CONCURRENT_TASK_BULK_UPDATE_BATCHES = 3;

interface TaskBulkUpdateBatch {
    workspaceId: string;
    taskIds: string[];
}

interface UpdateTasksAcrossWorkspacesParams {
    tasks: ReadonlyArray<Pick<Task, "id" | "workspaceId">>;
    action: TaskBulkUpdateAction;
    sendBatch: (
        params: TaskBulkUpdateBatch & { action: TaskBulkUpdateAction },
    ) => Promise<TaskBulkUpdateResponse>;
}

export interface TaskBulkUpdateAcrossWorkspacesResult {
    updatedTaskIds: string[];
    failedTaskIds: string[];
}

/**
 * The bulk update endpoint is per workspace, so the tasks are grouped by
 * workspace and sent in batches the server applies before it answers, a few
 * at a time. A batch that fails or comes back as a job (whose progress
 * arrives over the active workspace's channel, which the organization view
 * has none of) counts as not updated.
 */
export async function updateTasksAcrossWorkspaces({
    tasks,
    action,
    sendBatch,
}: UpdateTasksAcrossWorkspacesParams): Promise<TaskBulkUpdateAcrossWorkspacesResult> {
    const batches: TaskBulkUpdateBatch[] = Object.entries(
        groupBy(tasks, (task) => task.workspaceId),
    ).flatMap(([workspaceId, workspaceTasks]) =>
        chunk(workspaceTasks, TASK_BULK_UPDATE_SYNC_THRESHOLD).map(
            (batchTasks) => ({
                workspaceId,
                taskIds: batchTasks.map((task) => task.id),
            }),
        ),
    );
    const isAppliedByBatch: boolean[] = [];
    let nextBatchIndex = 0;

    const sendRemainingBatches = async () => {
        while (nextBatchIndex < batches.length) {
            const batchIndex = nextBatchIndex;
            nextBatchIndex += 1;
            isAppliedByBatch[batchIndex] = await sendBatch({
                ...batches[batchIndex],
                action,
            }).then(isTaskBulkUpdateApplied, () => false);
        }
    };

    await Promise.all(
        times(
            Math.min(MAX_CONCURRENT_TASK_BULK_UPDATE_BATCHES, batches.length),
            sendRemainingBatches,
        ),
    );

    return {
        updatedTaskIds: batches.flatMap((batch, index) =>
            isAppliedByBatch[index] ? batch.taskIds : [],
        ),
        failedTaskIds: batches.flatMap((batch, index) =>
            isAppliedByBatch[index] ? [] : batch.taskIds,
        ),
    };
}
